import { native } from '@forest/native-services'
import { Button, DialogShell, Spinner } from '@forest/shared/react'
import { Dialog } from 'radix-ui'
import { useEffect } from 'react'
import { JumpDialogDemo } from '~/components/jump-dialog-demo'
import gemIcon from '~/features/reward-flow/assets/gem.svg'
import { m } from '~/paraglide/messages'

const formatNumber = (n: number) => n.toLocaleString('en-US')

export function JumpConfirmDialog(props: {
  isOpen: boolean
  /** The jump point's target progress, in minutes. */
  requiredScore: number
  /** The jump point's price, in Time Crystals. */
  gemPrice: number
  isSubscriber: boolean
  /**
   * True while the purchase + score polling is in flight: both buttons lock
   * and the dialog refuses to dismiss.
   */
  isJumping: boolean
  onJump: () => void
  onClose: () => void
}) {
  useEffect(() => {
    if (!props.isOpen) return
    void native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        type: 'dialog',
        app_action: 'show',
        component_name: 'game_pass_boost_confirm_dialog',
      },
    })
  }, [props.isOpen])

  return (
    <DialogShell
      open={props.isOpen}
      onOpenChange={(nextIsOpen) =>
        !nextIsOpen && !props.isJumping && props.onClose()
      }
      overlayClassName="z-[55] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      className="z-[60] w-[300px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    >
      <div className="p-6 text-center">
        <JumpDialogDemo className="mx-auto h-[138px] w-[252px] max-w-full" />

        <Dialog.Title className="mt-4 text-lg font-semibold text-[#333]">
          {m.jump_confirmation_title({
            number: formatNumber(props.requiredScore),
          })}
        </Dialog.Title>

        <Dialog.Description className="mt-3 text-[#666]" asChild>
          <div>
            <p>
              {m.jump_confirmation_description({
                number: formatNumber(props.requiredScore),
              })}
            </p>
            {!props.isSubscriber && (
              <p className="mt-4 text-xs text-[#E84741]">
                {m.jump_confirmation_description_note()}
              </p>
            )}
          </div>
        </Dialog.Description>

        <div className="mt-4 flex flex-col items-center gap-3">
          <Button
            className="relative flex items-center justify-center gap-1"
            disabled={props.isJumping}
            onClick={() => {
              // Intent event: fires on the tap, whatever the purchase
              // outcome.
              void native.general.logEvent({
                event_name: 'component_event',
                event_params: {
                  type: 'dialog',
                  app_action: 'click',
                  component_name: 'game_pass_boost_confirm_dialog_purchase',
                },
              })
              props.onJump()
            }}
          >
            {props.isJumping && (
              <Spinner className="absolute right-7 text-[#176361]" />
            )}
            {m.btn_jump_now()}
            <img className="size-5" src={gemIcon} alt="" />
            {formatNumber(props.gemPrice)}
          </Button>

          <Dialog.Close asChild>
            <Button variant="ghost" size="sm" disabled={props.isJumping}>
              {m.cancel_btn()}
            </Button>
          </Dialog.Close>
        </div>
      </div>
    </DialogShell>
  )
}
